import { getLocalizedText } from "../../../getLocalizedText";
import {
  buildItemId,
  flattenArray,
  safeNumber,
  safeString,
  stripUndefined,
} from "../utils";

/**
 * Parsa fullCode nel formato: seasonCode_collectionCode_modelCode_materialCode_colorCode
 * es. "25P_HUM_PI001008U_12017_5877"
 */
const parseFullCode = (fullCode) => {
  if (!fullCode) return {};
  const parts = fullCode.split("_");
  if (parts.length < 5) return {};
  return {
    seasonCode: parts[0],
    collectionCode: parts[1],
    modelCode: parts[2],
    materialCode: parts[3],
    colorCode: parts[4],
  };
};

const extractVariant = (source, { catalogId, sizeRowId, colorCode }) => {
  if (!source?.catalog) {
    return undefined;
  }

  // Strategia di match a priorità decrescente:
  //
  // catalogId a livello di ROW ORDINE = ID colore-specifico (verificato: AvailabilityGridDoor
  //   filtra orderRowDetails per row.catalogId == colorSpecificId e funziona correttamente).
  // sizeRowId = indice di TAGLIA condiviso tra tutti i colori dello stesso prodotto
  //   → NON identifica univocamente colore+taglia da solo.
  // colorCode da parsedFullCode può essere sbagliato se il fullCode dell'ordine appartiene
  //   a un altro colore già presente nel carrello (es. vecchio colore 9402 quando si aggiunge 9200).
  //
  // Usa confronto string per evitare mismatch numero/stringa tra API e catalogo.

  // 1. catalogId + sizeRowId: match più preciso, usa l'ID colore dall'ordine (affidabile)
  if (catalogId != null && sizeRowId != null) {
    const normCatalogId = String(catalogId);
    const normSizeRowId = String(sizeRowId);
    const byCatalogAndSize = source.catalog.find(
      (item) =>
        item.catalogId != null &&
        String(item.catalogId) === normCatalogId &&
        item.sizeRowId != null &&
        String(item.sizeRowId) === normSizeRowId,
    );
    if (byCatalogAndSize) return byCatalogAndSize;
  }

  // 2. sizeRowId + colorCode (quando colorCode è affidabile, es. da detail.colorCode)
  if (sizeRowId != null && colorCode) {
    const normSizeRowId = String(sizeRowId);
    const bySizeAndColor = source.catalog.find(
      (item) =>
        item.sizeRowId != null &&
        String(item.sizeRowId) === normSizeRowId &&
        item.colorCode === colorCode,
    );
    if (bySizeAndColor) return bySizeAndColor;
  }

  // 3. Solo catalogId (colore corretto, taglia non specificata → prima taglia di quel colore)
  if (catalogId != null) {
    const normCatalogId = String(catalogId);
    const byCatalog = source.catalog.find(
      (item) =>
        item.catalogId != null && String(item.catalogId) === normCatalogId,
    );
    if (byCatalog) return byCatalog;
  }

  // 4. Solo sizeRowId (fallback: può restituire il colore sbagliato se sizeRowId è condiviso)
  if (sizeRowId != null) {
    const normSizeRowId = String(sizeRowId);
    const bySize = source.catalog.find(
      (item) =>
        item.sizeRowId != null && String(item.sizeRowId) === normSizeRowId,
    );
    if (bySize) return bySize;
  }

  // 5. Solo colorCode (prima taglia di quel colore)
  if (colorCode) {
    const byColor = source.catalog.find((item) => item.colorCode === colorCode);
    if (byColor) return byColor;
  }

  return source.catalog[0];
};

const normalisePrice = ({ totalPrice, quantity, unitPrice }) => {
  const parsedQuantity = safeNumber(quantity);
  const parsedUnitPrice = safeNumber(unitPrice);
  const parsedTotalPrice = safeNumber(totalPrice);

  if (parsedQuantity && parsedTotalPrice && !parsedUnitPrice) {
    return Number((parsedTotalPrice / parsedQuantity).toFixed(2));
  }

  if (parsedUnitPrice) {
    return parsedUnitPrice;
  }

  if (parsedTotalPrice && !parsedQuantity) {
    return parsedTotalPrice;
  }

  return undefined;
};

const buildBaseItem = ({ product, variant, language }) => {
  if (!product) {
    return undefined;
  }

  const localizedModelDescription = getLocalizedText(
    product.modelDescription,
    language,
  );

  const localizedCollectionDescription = getLocalizedText(
    product.collectionDescription,
    language,
  );

  const itemId = buildItemId({
    seasonCode: product.seasonCode,
    collectionCode: product.collectionCode,
    modelCode: product.modelCode,
    materialCode: product.materialCode,
    colorCode: variant?.colorCode,
  });

  return stripUndefined({
    item_id: itemId || safeString(product.productId),
    item_name: safeString(product.modelCode || localizedModelDescription),
    item_brand: safeString(product.brand || "Herno"),
    item_category: safeString(product.lineCode),
    item_category2: safeString(product.collectionCode),
    item_variant: safeString(variant?.colorCode),
    item_color: safeString(
      getLocalizedText(variant?.colorDescription, language) ||
        variant?.colorCode,
    ),
    item_model: safeString(product.modelCode),
    Cd_Modello: safeString(product.modelCode),
    Cd_Articolo: safeString(product.materialCode),
    Cd_Cartella: safeString(product.collectionCode),
    Cd_Linea: safeString(product.lineCode),
    Cd_Collezione: safeString(product.collectionCode),
    Season: safeString(product.seasonCode),
    item_description:
      safeString(localizedModelDescription) || safeString(product?.description),
  });
};

const enrichWithVariant = (item, variant, { quantity, context = {} }) => {
  if (!variant) {
    return item;
  }

  const enriched = {
    ...item,
    Taglia: safeString(variant.size),
    IndiceTaglia: safeString(variant.sizeRowId),
    Cd_Colore: safeString(variant.colorCode),
    Cd_Door: safeString(context?.doorCode),
  };

  if (quantity !== undefined) {
    enriched.quantity = quantity;
  }

  const priceValue = safeNumber(variant.price);
  if (priceValue !== undefined) {
    enriched.price = priceValue;
    enriched.Prezzo = Number(priceValue).toFixed(2);
  }

  return stripUndefined(enriched);
};

const mapVariantList = (product, variants, language, context = {}) =>
  variants
    .map((variant) => {
      const parsedQuantity = safeNumber(variant.quantity);
      const quantity = parsedQuantity === undefined ? 1 : parsedQuantity;

      return enrichWithVariant(
        buildBaseItem({ product, variant, language }),
        variant,
        {
          quantity,
          context,
        },
      );
    })
    .filter(Boolean);

export const mapProductDetailToViewItems = (
  product,
  { language, context } = {},
) => {
  if (!product) {
    return [];
  }

  const catalogVariants = Array.isArray(product.catalog)
    ? product.catalog.slice(0, 5)
    : [];

  if (catalogVariants.length > 0) {
    return mapVariantList(product, catalogVariants, language, context);
  }

  const baseItem = buildBaseItem({ product, language });
  return baseItem ? [baseItem] : [];
};

const resolvePriceValue = (item) => {
  const directPrice = safeNumber(item?.price);
  if (directPrice !== undefined) {
    return directPrice;
  }

  const formattedPrice = safeNumber(item?.Prezzo);
  if (formattedPrice !== undefined) {
    return formattedPrice;
  }

  const unitPrice = safeNumber(item?.unitPrice);
  if (unitPrice !== undefined) {
    return unitPrice;
  }

  return undefined;
};

export const serialiseProductItemForGtm = (
  item,
  {
    doorCode,
    includeName = false,
    includePricing = false,
    includeQuantita = true,
  } = {},
) => {
  if (!item) {
    return undefined;
  }

  const quantityValue =
    safeNumber(item.quantity) ?? safeNumber(item.Quantita) ?? 1;

  const priceValue = resolvePriceValue(item);

  const payload = {
    item_id: safeString(item.item_id),
    Cd_Modello: safeString(item.Cd_Modello),
    Cd_Articolo: safeString(item.Cd_Articolo),
    Cd_Cartella: "HERNO",
    Cd_Collezione: safeString(item.Cd_Collezione),
    Taglia: safeString(item.Taglia),
    IndiceTaglia: safeString(item.IndiceTaglia),
    Cd_Colore: safeString(item.Cd_Colore),
    Cd_Door: doorCode
      ? safeString(doorCode)
      : item.Cd_Door
        ? safeString(item.Cd_Door)
        : undefined,
    Prezzo:
      priceValue !== undefined
        ? priceValue.toFixed(2)
        : item.Prezzo !== undefined
          ? safeString(item.Prezzo)
          : undefined,
  };

  if (includeQuantita) {
    payload.Quantita = quantityValue;
  }

  if (includeName) {
    payload.item_name = safeString(item.Cd_Modello || item.item_name);
  }

  if (includePricing) {
    payload.price = priceValue !== undefined ? priceValue : undefined;
    payload.quantity = quantityValue;
  }

  return stripUndefined(payload);
};

const resolveProductFromCatalogues = (catalogueState, row) => {
  if (!row) {
    return undefined;
  }

  const { catalogueItemDetails = {}, catalogueItemDetailsSwap = {} } =
    catalogueState || {};

  const catalogueSources = {
    ...catalogueItemDetails,
    ...catalogueItemDetailsSwap,
  };

  if (row.productId && catalogueSources[row.productId]) {
    return catalogueSources[row.productId];
  }

  const catalogues = Object.values(catalogueSources);

  // row.productId non è mai salvato nel Redux reducer (il BE non lo restituisce
  // nei dettagli riga). L'unica fonte affidabile del modelCode è row.fullCode
  // (formato: seasonCode_collectionCode_modelCode_materialCode_colorCode).
  // Il modelCode identifica univocamente il prodotto nel catalogo.
  const fullCodeParts = row.fullCode ? row.fullCode.split("_") : [];
  const fullCodeModelCode = fullCodeParts.length >= 3 ? fullCodeParts[2] : null;
  const modelCodeToFind = row.modelCode ?? fullCodeModelCode;

  if (modelCodeToFind) {
    const byModelCode = catalogues.find(
      (item) => item.modelCode === modelCodeToFind,
    );
    if (byModelCode) return byModelCode;
  }

  // Fallback: sizeRowId (può dare il prodotto sbagliato se sizeRowId è condiviso
  // tra prodotti diversi, ma è l'unica opzione rimasta quando il catalogo non
  // ha ancora il modelCode caricato).
  return catalogues.find((item) =>
    Array.isArray(item?.catalog)
      ? item.catalog.some(
          (catalogVariant) =>
            row.sizeRowId != null &&
            catalogVariant.sizeRowId != null &&
            String(catalogVariant.sizeRowId) === String(row.sizeRowId),
        )
      : false,
  );
};

const extractRowDetail = (row) => {
  if (!row) {
    return undefined;
  }

  if (Array.isArray(row.orderRowsDetail) && row.orderRowsDetail.length > 0) {
    return row.orderRowsDetail[0];
  }

  return row;
};

export const mapOrderRowToItem = ({
  row,
  quantity,
  catalogueState,
  language,
}) => {
  if (!row || !quantity) {
    return undefined;
  }

  const detail = extractRowDetail(row);
  const parsedFullCode = parseFullCode(row.fullCode);

  // Codice modello atteso: priorità row > detail > fullCode
  const expectedModelCode =
    row.modelCode ?? detail?.modelCode ?? parsedFullCode.modelCode;

  const resolvedCatalogueProduct = resolveProductFromCatalogues(
    catalogueState,
    row,
  );

  // Se il catalogo restituisce un prodotto con modelCode diverso da quello atteso
  // (es. due modelli con stesso materialCode che condividono sizeRowId), i campi
  // identificativi del prodotto vengono presi dalla row. Il catalogue product viene
  // comunque usato per la ricerca della variante (size, colorCode, price) perché
  // sizeRowId/catalogId identificano univocamente taglia e colore indipendentemente
  // dal modello.
  const hasModelCodeMismatch =
    resolvedCatalogueProduct &&
    expectedModelCode &&
    resolvedCatalogueProduct.modelCode &&
    resolvedCatalogueProduct.modelCode !== expectedModelCode;

  const product = hasModelCodeMismatch
    ? {
        productId: row.productId ?? detail?.productId,
        modelCode: expectedModelCode,
        materialCode:
          row.materialCode ??
          detail?.materialCode ??
          parsedFullCode.materialCode,
        lineCode: row.lineCode ?? detail?.lineCode,
        collectionCode:
          row.collectionCode ??
          detail?.collectionCode ??
          parsedFullCode.collectionCode,
        seasonCode:
          row.seasonCode ?? detail?.seasonCode ?? parsedFullCode.seasonCode,
        modelDescription:
          row.modelDescription ?? detail?.modelDescription ?? row.description,
        collectionDescription:
          row.collectionDescription ?? detail?.collectionDescription,
      }
    : resolvedCatalogueProduct || {
        productId: row.productId ?? detail?.productId,
        modelCode: expectedModelCode,
        materialCode:
          row.materialCode ??
          detail?.materialCode ??
          parsedFullCode.materialCode,
        lineCode: row.lineCode ?? detail?.lineCode,
        collectionCode:
          row.collectionCode ??
          detail?.collectionCode ??
          parsedFullCode.collectionCode,
        seasonCode:
          row.seasonCode ?? detail?.seasonCode ?? parsedFullCode.seasonCode,
        modelDescription:
          row.modelDescription ?? detail?.modelDescription ?? row.description,
        collectionDescription:
          row.collectionDescription ?? detail?.collectionDescription,
      };

  // Per la ricerca della variante usa sempre il catalogue product se disponibile:
  // anche in caso di mismatch, sizeRowId/catalogId identificano correttamente
  // taglia e colore e ci permettono di recuperare size, price, colorCode.
  const catalogueProductForVariant = resolvedCatalogueProduct || product;

  // Cerca la variante nel catalogo; se il catalogo non è caricato, costruisce
  // una variante sintetica dai dati già presenti nella row/detail.
  //
  // detail.catalogId (da orderRowsDetail[i]) = ID colore+taglia specifico del singolo record,
  // uguale al catalogId inviato nella request (es. catalogId del colore 9200).
  // row.catalogId = action.payload.order.catalogId = ID a livello di INTERO ORDINE (cart),
  // identico per tutte le righe → NON usabile per identificare il colore.
  // Quindi detail.catalogId ha sempre priorità.
  const variantFromCatalogue = extractVariant(catalogueProductForVariant, {
    catalogId: detail?.catalogId ?? row.catalogId,
    sizeRowId: row.sizeRowId || detail?.sizeRowId,
    colorCode: detail?.colorCode ?? parsedFullCode.colorCode,
  });

  // Se la variante non è trovata nel catalogo del prodotto (es. catalogo non ancora
  // caricato al momento del remove_from_cart), prova a recuperare il campo `size`
  // da qualsiasi prodotto del catalogo che abbia lo stesso sizeRowId.
  // La taglia leggibile (es. "42") è identica per lo stesso sizeRowId
  // indipendentemente dal modello.
  const resolveSizeFromAnyCatalogue = (targetSizeRowId) => {
    if (!targetSizeRowId || !catalogueState) return undefined;
    const allProducts = {
      ...(catalogueState.catalogueItemDetails || {}),
      ...(catalogueState.catalogueItemDetailsSwap || {}),
    };
    const normTarget = String(targetSizeRowId);
    for (const p of Object.values(allProducts)) {
      if (!Array.isArray(p?.catalog)) continue;
      const found = p.catalog.find(
        (v) => v.sizeRowId != null && String(v.sizeRowId) === normTarget,
      );
      if (found?.size != null) return found.size;
    }
    return undefined;
  };

  const fallbackSizeRowId = detail?.sizeRowId ?? row.sizeRowId;
  const variant =
    variantFromCatalogue ??
    (detail
      ? {
          catalogId: detail.catalogId,
          sizeRowId: fallbackSizeRowId,
          colorCode: detail.colorCode ?? parsedFullCode.colorCode,
          size: detail.size ?? resolveSizeFromAnyCatalogue(fallbackSizeRowId),
          price: detail.price ?? row.price,
        }
      : undefined);

  const baseItem = buildBaseItem({ product, variant, language });
  if (!baseItem) {
    return undefined;
  }

  const unitPrice = normalisePrice({
    totalPrice: row.price || detail?.price,
    unitPrice: detail?.unitPrice,
    quantity,
  });

  const enriched = enrichWithVariant(baseItem, variant, {
    quantity,
    context: { doorCode: row.customData?.doorId || detail?.customData?.doorId },
  });

  return stripUndefined({
    ...enriched,
    price: unitPrice ?? enriched.price,
    Cd_Door:
      enriched.Cd_Door ||
      safeString(row.customData?.doorId || detail?.customData?.doorId),
    item_variant: enriched.item_variant || safeString(detail?.colorCode),
    item_size: enriched.Taglia,
  });
};

export const mapOrderRowsToItems = ({ rows, catalogueState, language }) => {
  if (!rows) {
    return [];
  }

  return flattenArray(
    rows.map((row) =>
      mapOrderRowToItem({
        row,
        quantity: row.quantity || row.currentQuantity,
        catalogueState,
        language,
      }),
    ),
  );
};

/**
 * Mappa una singola variante di prodotto (identificata da catalogId) per eventi GTM.
 * Usato per wishlist e subscribe_back_in_stock dove serve tracciare solo la variante specifica.
 */
export const mapSingleVariantToItem = ({
  product,
  catalogId,
  sizeRowId,
  language,
  context = {},
}) => {
  if (!product) {
    return undefined;
  }

  // catalogId è a livello di COLORE (stesso per tutte le taglie dello stesso colore).
  // sizeRowId identifica la taglia dentro quel colore.
  // Il match più preciso è la combinazione di entrambi; fallback progressivi.
  let variant;
  if (sizeRowId != null && catalogId) {
    // Combinazione esatta: colore + taglia
    variant = product.catalog?.find(
      (cat) =>
        cat.catalogId === catalogId &&
        cat.sizeRowId != null &&
        String(cat.sizeRowId) === String(sizeRowId),
    );
  }
  if (!variant && catalogId) {
    // Solo colore (restituisce la prima taglia di quel colore)
    variant = product.catalog?.find((cat) => cat.catalogId === catalogId);
  }
  if (!variant && sizeRowId != null) {
    // Solo taglia (ultimo fallback)
    variant = product.catalog?.find(
      (cat) =>
        cat.sizeRowId != null && String(cat.sizeRowId) === String(sizeRowId),
    );
  }

  if (!variant) {
    return undefined;
  }

  const baseItem = buildBaseItem({ product, variant, language });
  if (!baseItem) {
    return undefined;
  }

  return enrichWithVariant(baseItem, variant, {
    quantity: 1,
    context,
  });
};
